import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { edifyPackagesGetProjectByPackageId } from "@/usersAndProjects-client/sdk.gen";

export interface ProjectPackageInfo {
  projectName: string;
  packageName: string;
}

export interface UseBulkFetchProjectsByPackageIdsResult {
  projectPackageMap: Map<string, ProjectPackageInfo>;
  isLoading: boolean;
}

/**
 * Bulk-fetch project names for a list of packageIds by firing parallel
 * edifyPackagesGetProjectByPackageId requests. Package names are resolved
 * separately via useFetchPackages.
 *
 * Returns a Map<packageId, { projectName, packageName }> where packageName
 * is looked up from the provided packagesMap.
 */
export const useBulkFetchProjectsByPackageIds = (
  packageIds: string[],
  packagesMap: Map<string, string>,
): UseBulkFetchProjectsByPackageIdsResult => {
  const uniquePackageIds = useMemo(
    () => Array.from(new Set(packageIds.filter(Boolean))),
    [packageIds],
  );

  const queries = useQueries({
    queries: uniquePackageIds.map((packageId) => ({
      queryKey: ["project-by-package", packageId],
      queryFn: async () => {
        const response = await edifyPackagesGetProjectByPackageId({
          path: { packageId },
        });
        if (!response.data || typeof response.data !== "object" || !("project" in response.data)) {
          return null;
        }
        return response.data;
      },
      enabled: !!packageId,
      retry: false,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);

  const projectPackageMap = useMemo(() => {
    const map = new Map<string, ProjectPackageInfo>();
    for (let i = 0; i < uniquePackageIds.length; i++) {
      const packageId = uniquePackageIds[i];
      const result = queries[i];
      if (result.data?.project) {
        map.set(packageId, {
          projectName: result.data.project.name,
          packageName: packagesMap.get(packageId) || "",
        });
      }
    }
    return map;
  }, [queries, packagesMap, uniquePackageIds]);

  return {
    projectPackageMap,
    isLoading,
  };
};
